import { ButtonPrimary } from '@/components/button'
import { LoadingScreen } from '@/components/common'
import { MainContent } from '@/components/content'
import { ArrowBack, Calendar } from '@/components/icons'
import {
  AutocompleteAddress,
  AutocompleteLocationInput,
  GenderSwitch,
  LogoInput,
  TextInput,
  VerifyPhoneInput,
} from '@/components/input'
import { MainLayout } from '@/components/layouts'
import { SettingSidebar } from '@/components/sidebar'
import { duprCountries } from '@/constants/Countries'
import { APP_CUSTOM_ERROR } from '@/constants/MessageConstants'
import { ROUTE_DASHBOARD, ROUTE_VERIFY_PHONE } from '@/constants/Routes'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { setShowDropDown } from '@/pages/home/appSlice'
import { uploadImage, useSaveAddress, useUpdateProfile } from '@/services'
import { Country, Gender, PayloadUpdateProfile } from '@/types'
import EditProfileValidationSchema from '@/utils/schema/EditProfileValidationSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import { MutateOptions } from '@tanstack/react-query'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'
import { ContactSupportModal } from '../../../components/modal'
import { userSelector } from '../selectors'
import { updateUser } from '../userSlice'
import { useHasFeature } from '@/hooks/useHasFeature'
import { Feature } from '@/config/features'

interface ProfileFormInputs {
  mediaId?: number
  birthdate: string
  gender: Gender
  phone: string
  location: AutocompleteAddress
  fullName: string
}

export const Profile = () => {
  const { t } = useTranslation()

  const dispatch = useAppDispatch()
  const { user } = useAppSelector(userSelector)
  const { mutate: submit } = useUpdateProfile()
  const { mutate: saveAddress } = useSaveAddress()

  const [selectedCountry, setSelectedCountry] = useState<Country>(
    duprCountries.find((item) => item.isoCode === user?.isoCode) || duprCountries[0]
  )
  const [isValidPhone, setIsValidPhone] = useState<boolean>(user?.isValidPhone ? user?.isValidPhone : false)
  const [isVerifyPhoneModalOpened, setisVerifyPhoneModalOpened] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [showModal, setShowModal] = useState<'BIRTH_YEAR' | 'NAME' | ''>('')

  const fullName =
    user?.fullName?.trim() && user?.fullName?.trim() !== 'undefined undefined' && user?.fullName?.trim() !== 'null null'
      ? user?.fullName?.trim()
      : ''

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors, defaultValues, isDirty },
  } = useForm<ProfileFormInputs>({
    defaultValues: {
      birthdate: user?.birthdate || '',
      gender: user?.gender || Gender.MALE,
      phone: user ? user?.phone?.split(selectedCountry.dialCode).pop() : '',
      location: user
        ? { description: user?.addresses[0]?.formattedAddress, placeId: user?.addresses[0]?.placeId }
        : { description: '', placeId: '' },
      fullName: fullName,
    },
    mode: 'onChange',
    resolver: yupResolver(EditProfileValidationSchema as any),
  })

  const watchGender = watch('gender', defaultValues?.gender)
  const watchPhone = watch('phone', defaultValues?.phone)

  const onSubmit: SubmitHandler<ProfileFormInputs> = async (data) => {
    setIsLoading(true)
    data.birthdate = moment(data.birthdate) ? moment(data.birthdate).format('YYYY-MM-DD') : data.birthdate

    const updatedInfo = {
      gender: data.gender,
      birthdate: data.birthdate,
      phone: data.phone ? selectedCountry.dialCode + data.phone : '',
      isoCode: selectedCountry.isoCode,
      fullName: data.fullName,
    }

    const updateProfileOptions: MutateOptions<any, unknown, PayloadUpdateProfile> = {
      onSuccess: (data) => {
        dispatch(updateUser(data.result))
        toast.success('Updated')
        setIsLoading(false)
      },
      onError: (error: any) => {
        toast.error(error.response?.data.message || APP_CUSTOM_ERROR)
        setIsLoading(false)
      },
    }
    if (data.location.placeId) {
      saveAddress(
        { placeId: data.location.placeId },
        {
          onSuccess: (data) => {
            const reqData = {
              ...updatedInfo,
              addressId: data.id,
            }
            submit(reqData, updateProfileOptions)
          },
        }
      )
    } else {
      submit(updatedInfo, updateProfileOptions)
    }
  }

  const navigate = useNavigate()

  // Build current E.164 from selected country + form phone
  const currentE164 = `${selectedCountry.dialCode}${watchPhone ?? ''}`.trim()

  const showPhoneVerify = useHasFeature(Feature.PhoneVerifyRolloutPt1)

  // Show verify button only if backend says not verified
  const showVerifyButton = !isValidPhone && showPhoneVerify

  const onShowModal = (type: 'BIRTH_YEAR' | 'NAME') => setShowModal(type)

  const onSaveLogo = (file: File) => {
    setIsLoading(true)
    uploadImage(file as File).then((res) => {
      const mediaId = res.result.id

      const reqData = {
        fullName: fullName,
        mediaId: mediaId,
      }
      submit(reqData as any, {
        onSuccess: (data) => {
          dispatch(updateUser(data.result))
          toast.success('Updated')
          setIsLoading(false)
        },
        onError: (error: any) => {
          toast.error(error.response?.data.message || APP_CUSTOM_ERROR)
          setIsLoading(false)
        },
      })
    })
  }

  const onRemoveProfileLogo = () => {
    setIsLoading(true)
    const reqData = {
      fullName: fullName,
      mediaId: -1,
    }
    submit(reqData as any, {
      onSuccess: (data) => {
        dispatch(updateUser(data[0].result))
        toast.success('Removed Profile Picture')
        setIsLoading(false)
      },
      onError: (error: any) => {
        toast.error(error.response?.data.message || APP_CUSTOM_ERROR)
        setIsLoading(false)
      },
    })
  }

  useEffect(() => {
    setSelectedCountry(duprCountries.find((item) => item.isoCode === user?.isoCode) || duprCountries[0])
  }, [user?.isoCode])

  useEffect(() => {
    reset({
      fullName: fullName,
      birthdate: user?.birthdate || '',
      gender: user?.gender || Gender.MALE,
      phone:
        user?.phone
          ?.split(duprCountries.find((item) => item.isoCode === user?.isoCode)?.dialCode || duprCountries[0].dialCode)
          .pop() || '',
      location: { description: user?.addresses[0]?.formattedAddress, placeId: user?.addresses[0]?.placeId },
    })
    setIsValidPhone(!!user?.isValidPhone)
  }, [fullName, reset, user])

  return (
    <>
      <MainLayout>
        <SettingSidebar />
        {user ? (
          <MainContent>
            <div className="flex w-full flex-col items-center">
              <div
                className={`flex flex-col w-full lg:w-[480px] gap-y-4 ml-0 transition-all duration-500 ease-in-out mb-20 lg:mb-8`}
              >
                <div className="flex items-center gap-x-3">
                  <Link to={ROUTE_DASHBOARD} className="flex sm:hidden" onClick={() => dispatch(setShowDropDown(''))}>
                    <ArrowBack className="text-white" />
                  </Link>
                  <h1 className="text-2xl md:text-[32px] md:leading-10 font-bold text-white">
                    {t('user.editProfile')}
                  </h1>
                </div>

                <LogoInput
                  defaultSrc={user?.imageUrl}
                  onSave={onSaveLogo}
                  onRemoveProfileLogo={onRemoveProfileLogo}
                  uploadImageModalTitle="Profile Picture"
                />

                <form className="flex flex-col gap-y-4" onSubmit={handleSubmit(onSubmit)}>
                  <TextInput
                    placeholder={fullName}
                    label={t('common.fullName')}
                    {...register('fullName')}
                    error={errors.fullName?.message}
                    readOnly={true}
                    className="name-input"
                    onClick={() => {
                      onShowModal('NAME')
                    }}
                  />
                  <Tooltip anchorSelect=".name-input" place="bottom" className="z-10">
                    Please contact support to request a name change.
                  </Tooltip>
                  <ContactSupportModal open={showModal} onClose={() => setShowModal('')} />
                  <Controller
                    name="location"
                    control={control}
                    render={({ field }) => (
                      <AutocompleteLocationInput
                        {...field}
                        regionsOnly
                        label={t('common.location')}
                        setValue={(location) =>
                          setValue('location', location, { shouldDirty: true, shouldValidate: true })
                        }
                        error={errors.location?.description?.message}
                      />
                    )}
                  />
                  <div className="flex flex-col gap-y-2">
                    <span className={`text-white text-sm font-semibold`}>{t('common.birthday')}</span>
                    <div
                      className="flex rounded-full bg-white py-[18px] pl-12 pr-[17px] relative birth-date text-navy-900 font-normal text-sm"
                      onClick={() => onShowModal('BIRTH_YEAR')}
                    >
                      <Calendar
                        className={`absolute top-1/2 -translate-y-1/2 left-4 z-10 pointer-events-none text-navy-900 flex-wrap`}
                      />
                      {moment(user?.birthdate).format('YYYY-MM-DD')}
                      <Tooltip anchorSelect=".birth-date" place="bottom" className="z-10">
                        Please contact support to request a birth date change.
                      </Tooltip>
                    </div>
                  </div>
                  <GenderSwitch
                    label={t('common.gender')}
                    value={watchGender}
                    // setValue={(gender: Gender) => setValue('gender', gender, { shouldDirty: true })}
                    setValue={() => {}}
                    disabled={true}
                  />

                  <VerifyPhoneInput
                    label={t('common.phone')}
                    placeholder={t('common.yourPhone')}
                    maxLength={15}
                    selectedCountry={selectedCountry}
                    setSelectedCountry={setSelectedCountry}
                    phoneNumber={watchPhone}
                    setPhoneNumber={(phone: string) =>
                      setValue('phone', phone, { shouldDirty: true, shouldValidate: true })
                    }
                    isValidPhone={isValidPhone}
                    setIsValidPhone={setIsValidPhone}
                    resendVerifyCountDownMinutes={5}
                    error={errors.phone?.message}
                    userPhoneNumber={user.phone ? user.phone : ''}
                    isVerifyPhoneModalOpened={isVerifyPhoneModalOpened}
                    setIsVerifyPhoneModalOpened={setisVerifyPhoneModalOpened}
                  />
                  {showVerifyButton && (
                    <div className="flex">
                      <ButtonPrimary
                        type="button"
                        title={t('common.verifyYourPhone', 'Verify your phone')}
                        className="px-6"
                        onClick={() => {
                          // Prefer current form phone if present, else fall back to saved user.phone
                          const phoneToVerify = currentE164 || user.phone || ''
                          const iso = selectedCountry.isoCode || user.isoCode || ''
                          const qs = new URLSearchParams({ phone: phoneToVerify, iso }).toString()
                          navigate(`${ROUTE_VERIFY_PHONE}?${qs}`)
                        }}
                        // If there is literally no phone to verify, keep it disabled
                        disabled={!currentE164 && !user.phone}
                      />
                    </div>
                  )}
                  <div className="flex justify-end">
                    <ButtonPrimary
                      title={t('common.save')}
                      className="px-6"
                      type="submit"
                      disabled={!isDirty && user?.phone?.includes(selectedCountry.dialCode)}
                    />
                  </div>
                </form>
              </div>
            </div>
          </MainContent>
        ) : (
          <></>
        )}
        {isLoading && <LoadingScreen />}
      </MainLayout>
    </>
  )
}
